import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  Avatar,
  Chip,
  Divider,
  Alert,
  InputAdornment,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { calculateStock } from "../utils";

/**
 * Modal para recibir stock (entrada de inventario)
 */
const ReceiveStockModal = ({ open, onClose, product, technicians = [], locations = [], onSubmit }) => {
  const [formData, setFormData] = useState({
    toLocationType: "main_warehouse", // main_warehouse, technician, dangerous_goods
    toLocationId: null, // ID específico del almacén
    toTechnicianId: null,
    quantity: 0,
    reason: "",
  });

  const [error, setError] = useState("");

  useEffect(() => {
    if (product && locations) {
      // Seleccionar el primer almacén disponible por defecto
      const activeWarehouses = locations.filter(loc => loc.type === "main_warehouse" && loc.status === "active");
      const firstWarehouse = activeWarehouses.length > 0 ? activeWarehouses[0] : null;
      
      setFormData({
        toLocationType: "main_warehouse",
        toLocationId: firstWarehouse?._id || null,
        toTechnicianId: null,
        quantity: 0,
        reason: "",
      });
      setError("");
    }
  }, [product, technicians, locations]);

  if (!product) return null;

  const { warehouse: totalWarehouseStock, technicians: techStock, total } = calculateStock(product);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
  };

  const validateReceive = () => {
    // Validar que se haya seleccionado un almacén si es necesario
    if (formData.toLocationType === "main_warehouse" && !formData.toLocationId) {
      setError("Debes seleccionar un almacén de destino");
      return false;
    }

    // Validar que se haya seleccionado un técnico si es necesario
    if (formData.toLocationType === "technician" && !formData.toTechnicianId) {
      setError("Debes seleccionar un técnico de destino");
      return false;
    }

    if (formData.quantity <= 0) {
      setError("La cantidad debe ser mayor a 0");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateReceive()) return;

    // Obtener nombre del técnico de destino
    const toTechnician = technicians.find((t) => t._id === formData.toTechnicianId);
    const toTechnicianName = toTechnician?.name || "";

    onSubmit({
      itemId: product._id,
      toLocationType: formData.toLocationType,
      toLocationId: formData.toLocationId,
      toTechnicianId: formData.toTechnicianId,
      toTechnicianName: toTechnicianName,
      quantity: formData.quantity,
      reason: formData.reason || null,
    });
  };

  const getLocationIcon = (locationType) => {
    if (locationType === "main_warehouse") return <WarehouseIcon />;
    if (locationType === "dangerous_goods") return <DeleteForeverIcon />;
    return <EngineeringIcon />;
  };

  const locationTypes = [
    { value: "main_warehouse", label: "Almacén Principal", icon: <WarehouseIcon /> },
    { value: "technician", label: "Técnico", icon: <EngineeringIcon /> },
    { value: "dangerous_goods", label: "Residuos Peligrosos", icon: <DeleteForeverIcon /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "success.main",
          color: "white",
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 48,
              height: 48,
            }}
          >
            <AddShoppingCartIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Recibir Stock
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {product.name}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Información del producto */}
        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            display="block"
            mb={1}
          >
            Stock Actual
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="space-around">
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {totalWarehouseStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                En Almacenes
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="info.main">
                {techStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Con Técnicos
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Formulario de recepción */}
        <Stack spacing={3}>
          {/* Destino - Tipo de ubicación */}
          <FormControl fullWidth>
            <InputLabel>Destino</InputLabel>
            <Select
              value={formData.toLocationType}
              onChange={(e) => {
                handleChange("toLocationType", e.target.value);
                handleChange("toTechnicianId", null);
                handleChange("toLocationId", null);
                
                if (e.target.value === "technician" && technicians.length > 0) {
                  handleChange("toTechnicianId", technicians[0]._id);
                } else if (e.target.value === "main_warehouse") {
                  // Seleccionar el primer almacén si se elige main_warehouse
                  const firstWarehouse = locations.find(loc => loc.type === "main_warehouse");
                  handleChange("toLocationId", firstWarehouse?._id || null);
                }
              }}
              label="Destino"
            >
              {locationTypes.map((loc) => (
                <MenuItem key={loc.value} value={loc.value}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {loc.icon}
                    <Typography>{loc.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Seleccionar almacén de destino (si aplica) */}
          {formData.toLocationType === "main_warehouse" && (
            <FormControl fullWidth>
              <InputLabel>Almacén de destino</InputLabel>
              <Select
                value={formData.toLocationId || ""}
                onChange={(e) => handleChange("toLocationId", e.target.value)}
                label="Almacén de destino"
              >
                {locations
                  .filter(loc => loc.type === "main_warehouse" && loc.status === "active")
                  .map((location) => {
                    // Buscar el stock de este almacén en el producto
                    const warehouseStock = product.warehouses?.find(
                      (w) => (w.locationId?.toString() || w.locationId) === location._id
                    );
                    const stock = warehouseStock?.stock || 0;
                    
                    return (
                      <MenuItem key={location._id} value={location._id}>
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <WarehouseIcon fontSize="small" />
                            <Typography>{location.name || "Almacén Principal"}</Typography>
                          </Stack>
                          <Chip 
                            label={`${stock} ${product.unit || "u"}`} 
                            size="small" 
                            color="info"
                          />
                        </Stack>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          )}

          {/* Seleccionar técnico de destino (si aplica) */}
          {formData.toLocationType === "technician" && (
            <FormControl fullWidth>
              <InputLabel>Técnico de destino</InputLabel>
              <Select
                value={formData.toTechnicianId || ""}
                onChange={(e) => handleChange("toTechnicianId", e.target.value)}
                label="Técnico de destino"
              >
                {technicians.map((tech) => {
                  const techStock = product.technicians?.find(
                    (t) => (t.technicianId?.toString() || t.technicianId) === tech._id
                  );
                  const stock = techStock?.stock || 0;
                  return (
                    <MenuItem key={tech._id} value={tech._id}>
                      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <EngineeringIcon fontSize="small" />
                          <Typography>{tech.name}</Typography>
                        </Stack>
                        <Chip 
                          label={`${stock} ${product.unit || "u"}`} 
                          size="small" 
                          color="info"
                        />
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Cantidad */}
          <TextField
            label="Cantidad a recibir"
            type="number"
            value={formData.quantity}
            onChange={(e) => handleChange("quantity", parseFloat(e.target.value) || 0)}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Chip label={product.unit || "u"} size="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              step: product.unit === "ml" || product.unit === "lt" ? 0.1 : 1,
            }}
          />

          {/* Notas */}
          <TextField
            label="Notas (opcional)"
            value={formData.reason}
            onChange={(e) => handleChange("reason", e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="Ej: Compra nueva, reabastecimiento, devolución..."
          />

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          size="large"
          startIcon={<AddShoppingCartIcon />}
          disabled={!formData.quantity || (formData.toLocationType === "technician" && !formData.toTechnicianId)}
        >
          Recibir Stock
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiveStockModal;

